import log, { LogLevelDesc } from 'loglevel';
import dayjs from 'dayjs';

import { env } from 'env';
import { recordBrowserLogLine } from '@services/reportIt/browserLogBuffer';
import { formatLogArgs, serializeLogArg } from './logSerialize';

const { REACT_APP_LOG_LEVEL = log.levels.DEBUG } = env;

const originalFactory = log.methodFactory;
log.noConflict();

log.methodFactory = (methodName, logLevel, loggerName) => {
  return (message?: unknown, ...rest: unknown[]) => {
    // Resolved on every call, not once when the factory runs: this module can
    // evaluate before Sentry.init() patches `console.*` (module graph order
    // depends on who imports what — see MV-2088), so caching the method here
    // would permanently bind to the pre-patch console and silently bypass
    // Sentry's console capture for the page's whole lifetime.
    const consoleMethod = originalFactory(methodName, logLevel, loggerName);

    const name = loggerName ? String(loggerName) : 'default';
    const date = dayjs().format('YYYY-MM-DD HH:mm:ss');

    const finalMessage = [
      `[${date}]`,
      `[${name}]`,
      serializeLogArg(message),
    ].join(' ');

    // The ring buffer is text only, so trailing arguments (an Error, a Zoom
    // SDK reject payload, a `{ sessionName, error }` context object) have to be
    // flattened here — passed through untouched they never reached the report
    // and the log line arrived with no error detail at all (MV-1740).
    const details = formatLogArgs(rest);
    const capturedLine = details ? `${finalMessage} ${details}` : finalMessage;

    // Mirror into the Report It ring buffer (MV-1396) — best-effort; a capture
    // failure must never break logging.
    try {
      recordBrowserLogLine(capturedLine);
    } catch {
      // ignore
    }

    // The console gets the live values, not the serialised copy, so devtools
    // keeps its expandable object/stack view.
    if (rest.length > 0) {
      consoleMethod(finalMessage, ...rest);
    } else {
      consoleMethod(finalMessage);
    }
  };
};

// setLevel binds each method through the factory, so it must run *after* the
// factory is replaced — otherwise the root logger keeps loglevel's default
// methods and its output bypasses the ring buffer entirely.
log.setLevel(REACT_APP_LOG_LEVEL as LogLevelDesc);
log.rebuild();

const MytaverseLogger = log.getLogger('Mytaverse');

export default log;
export { MytaverseLogger };
